import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useCountdown } from '../lib/useCountdown';
import { monthlyPlansStore } from '../model';
import { useSendSalesPlans } from '../../../../screens/ProfileScreen/entities/user/api';
import { useSnackbar } from 'notistack';
import { MonthlyPlansWidget } from './MonthlyPlansWidget';
import { WelcomeDrawer } from './WelcomeDrawer';
import { PlansDrawer } from './PlansDrawer';

interface FormValues {
  profit: string;
  revenue: string;
}

interface MonthlyPlansContainerProps {
  visible: boolean;
  branch: 'urfo' | 'msk';
  data: FormValues;
}

export const MonthlyPlansContainer = observer(
  ({ visible, branch, data }: MonthlyPlansContainerProps) => {
    const isMsk = branch === 'msk';
    const [isWelcomeDrawerOpen, setIsWelcomeDrawerOpen] = useState(false);
    const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false);

    const [displayValues, setDisplayValues] = useState<FormValues>({
      profit: data.profit || '',
      revenue: isMsk ? '' : data.revenue || '',
    });

    const { timeLeft, isExpired } = useCountdown();
    const { enqueueSnackbar } = useSnackbar();
    const { mutateAsync: sendSalesPlans } = useSendSalesPlans();

    const hasPlanValues = isMsk
      ? Boolean(displayValues.profit)
      : Boolean(displayValues.profit || displayValues.revenue);

    const handleSubmit = async (values: FormValues) => {
      const previousValues = { ...displayValues };

      const revenue = isMsk ? '' : values.revenue;

      monthlyPlansStore.setProfit(values.profit);
      monthlyPlansStore.setRevenue(revenue);

      setDisplayValues({
        profit: values.profit,
        revenue,
      });

      setIsFormDrawerOpen(false);

    try {
      await sendSalesPlans(monthlyPlansStore.getFormData());
      enqueueSnackbar('Планы успешно сохранены', {
        variant: 'success',
      });
    } catch (e) {
      setDisplayValues(previousValues);
      enqueueSnackbar(e instanceof Error ? e.message : 'Ошибка при сохранении планов', {
        variant: 'error',
      });
    }
    };

    if (!visible) return null;

    const handleWidgetClick = () => {
      if (hasPlanValues) {
        setIsFormDrawerOpen(true);
      } else {
        setIsWelcomeDrawerOpen(true);
      }
    };

    const handleCloseWidget = () => {
      monthlyPlansStore.closeWidget();
    };

    const handleOpenForm = () => {
      setIsWelcomeDrawerOpen(false);
      setIsFormDrawerOpen(true);
    };

    const handleCloseForm = () => {
      setIsFormDrawerOpen(false);
    };

    const isWidgetShown = monthlyPlansStore.isVisible && !isExpired;

    return (
      <>
        <MonthlyPlansWidget
          timeLeft={timeLeft}
          isWidgetVisible={isWidgetShown}
          onClick={handleWidgetClick}
          onClose={handleCloseWidget}
        />

        <WelcomeDrawer
          open={isWelcomeDrawerOpen}
          onClose={() => setIsWelcomeDrawerOpen(false)}
          onOpenForm={handleOpenForm}
          timeLeft={timeLeft}
        />

        <PlansDrawer
          open={isFormDrawerOpen}
          onClose={handleCloseForm}
          onOpen={() => setIsFormDrawerOpen(true)}
          branch={branch}
          initialValues={displayValues}
          onSubmit={handleSubmit}
        />
      </>
    );
  },
);
